/**
 * Selection + keyboard navigation state for an overlay layer.
 *
 * Roving-tabindex pattern: the layer owns a single "focused id" and a
 * single "selected id" (they're allowed to differ — keyboard nav moves
 * focus eagerly but only Enter / pointer click promotes that into a
 * selection). This matches the WAI-ARIA listbox guidance and works
 * cleanly when the same layer is used for both highlights (read-only
 * focus stops) and editable form fields.
 *
 * State is intentionally kept dependency-free so it can be unit tested
 * with `renderHook` and reused inside controlled wrappers (e.g. when the
 * detail page wants to drive selection from a Fields panel click).
 */

import { useCallback, useMemo, useState } from 'react'

import type { OverlayItemModel, OverlayId } from './types'

export interface UseOverlaySelectionArgs {
  /**
   * Overlays in the order they should be traversed by keyboard nav.
   * Items with `selectable === false` are skipped during arrow / tab
   * traversal but remain addressable by `select(id)`.
   */
  items: OverlayItemModel[]
  /** Initial selection (uncontrolled mode). */
  initialSelectedId?: OverlayId | null
  /** Controlled selection from a parent — overrides internal state. */
  selectedId?: OverlayId | null
  onSelectionChange?: (id: OverlayId | null) => void
}

export interface UseOverlaySelectionApi {
  /** Currently selected overlay id, or `null` when nothing is selected. */
  selectedId: OverlayId | null
  /**
   * Currently focused overlay id. Selection lags focus by design — only
   * Enter / pointer click promotes the focus into a selection.
   */
  focusedId: OverlayId | null
  /** Set selection programmatically. */
  select: (id: OverlayId | null) => void
  /** Move keyboard focus without changing selection. */
  focus: (id: OverlayId | null) => void
  /** Move focus to the next selectable item (wraps). */
  focusNext: () => void
  /** Move focus to the previous selectable item (wraps). */
  focusPrev: () => void
  /** Move focus to the first selectable item. */
  focusFirst: () => void
  /** Move focus to the last selectable item. */
  focusLast: () => void
  /** Returns true if the id is the current focus stop. */
  isFocused: (id: OverlayId) => boolean
  /** Returns true if the id is the current selection. */
  isSelected: (id: OverlayId) => boolean
}

function isSelectable(item: OverlayItemModel): boolean {
  if (item.selectable === false) return false
  if (item.selectable === true) return true
  // Default: every kind except passive highlights is selectable.
  return item.kind !== 'highlight'
}

export function useOverlaySelection(
  args: UseOverlaySelectionArgs,
): UseOverlaySelectionApi {
  const {
    items,
    initialSelectedId = null,
    selectedId: controlledSelectedId,
    onSelectionChange,
  } = args

  const controlled = controlledSelectedId !== undefined
  const [internalSelectedId, setInternalSelectedId] = useState<OverlayId | null>(
    initialSelectedId,
  )
  const selectedId = controlled ? controlledSelectedId : internalSelectedId

  // Focus tracks selection by default, but the caller can drive it ahead
  // of selection via `focus()` for roving-tabindex semantics.
  const [focusedId, setFocusedId] = useState<OverlayId | null>(
    initialSelectedId,
  )

  // Memoize the selectable-id list so callbacks below only invalidate when
  // the underlying order actually shifts (not on every render).
  const selectableOrder = useMemo<OverlayId[]>(
    () => items.filter(isSelectable).map((item) => item.id),
    [items],
  )

  const select = useCallback(
    (id: OverlayId | null) => {
      if (!controlled) setInternalSelectedId(id)
      if (id !== null) setFocusedId(id)
      onSelectionChange?.(id)
    },
    [controlled, onSelectionChange],
  )

  const focus = useCallback((id: OverlayId | null) => {
    setFocusedId(id)
  }, [])

  const focusFirst = useCallback(() => {
    if (selectableOrder.length === 0) return
    setFocusedId(selectableOrder[0] ?? null)
  }, [selectableOrder])

  const focusLast = useCallback(() => {
    if (selectableOrder.length === 0) return
    setFocusedId(selectableOrder[selectableOrder.length - 1] ?? null)
  }, [selectableOrder])

  const focusNext = useCallback(() => {
    if (selectableOrder.length === 0) return
    setFocusedId((prev) => {
      if (prev == null) return selectableOrder[0] ?? null
      const idx = selectableOrder.indexOf(prev)
      if (idx === -1) return selectableOrder[0] ?? null
      const nextIdx = (idx + 1) % selectableOrder.length
      return selectableOrder[nextIdx] ?? null
    })
  }, [selectableOrder])

  const focusPrev = useCallback(() => {
    if (selectableOrder.length === 0) return
    setFocusedId((prev) => {
      if (prev == null)
        return selectableOrder[selectableOrder.length - 1] ?? null
      const idx = selectableOrder.indexOf(prev)
      if (idx === -1)
        return selectableOrder[selectableOrder.length - 1] ?? null
      const prevIdx = (idx - 1 + selectableOrder.length) % selectableOrder.length
      return selectableOrder[prevIdx] ?? null
    })
  }, [selectableOrder])

  const isFocused = useCallback(
    (id: OverlayId) => focusedId === id,
    [focusedId],
  )
  const isSelected = useCallback(
    (id: OverlayId) => selectedId === id,
    [selectedId],
  )

  return useMemo(
    () => ({
      selectedId,
      focusedId,
      select,
      focus,
      focusNext,
      focusPrev,
      focusFirst,
      focusLast,
      isFocused,
      isSelected,
    }),
    [
      selectedId,
      focusedId,
      select,
      focus,
      focusNext,
      focusPrev,
      focusFirst,
      focusLast,
      isFocused,
      isSelected,
    ],
  )
}
